import { ClientApplication } from 'schema/serverTypes';

export interface VersionNumber {
  major: number;
  minor: number;
  patch: number;
}

export const formatVersion = ({ major, minor, patch }: VersionNumber): string =>
  `${major}.${minor}.${patch}`;

export const compareVersions = (a: VersionNumber, b: VersionNumber): number =>
  a.major - b.major || a.minor - b.minor || a.patch - b.patch;

export const parseVersion = (value: string): VersionNumber | null => {
  if (!/^\d+\.\d+\.\d+$/.test(value) || value.trim() !== value) return null;
  const parts = value.split('.').map(Number);
  if (parts.some((part) => !Number.isSafeInteger(part) || part > 2147483647)) return null;
  return { major: parts[0], minor: parts[1], patch: parts[2] };
};

export type UpdateRequirement = 'mandatory' | 'optional' | 'none';

export interface UpdateZone {
  requirement: UpdateRequirement;
  range: string;
}

export const getUpdateZones = (
  latest: VersionNumber,
  mandatory?: VersionNumber | null
): UpdateZone[] => {
  const latestLabel = formatVersion(latest);
  const currentZone: UpdateZone = { requirement: 'none', range: `${latestLabel} и новее` };
  if (!mandatory) {
    return [{ requirement: 'optional', range: `ниже ${latestLabel}` }, currentZone];
  }
  const mandatoryLabel = formatVersion(mandatory);
  const mandatoryZone: UpdateZone = { requirement: 'mandatory', range: `ниже ${mandatoryLabel}` };
  return compareVersions(mandatory, latest) < 0
    ? [
        mandatoryZone,
        { requirement: 'optional', range: `от ${mandatoryLabel} до ${latestLabel}` },
        currentZone,
      ]
    : [mandatoryZone, currentZone];
};

export const platformLabel = (client: ClientApplication): string =>
  client === ClientApplication.Android ? 'Android' : 'iOS';
